import React, {memo, SyntheticEvent, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {ComposerPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {FlexSpacer} from '../../../common/flex';
import {GridAreaItem} from '../../../common/grid';

/*
 * Props.
 */

export interface SettingsComposerToolbarProps {
  panelId?: ComposerPanelId | ViewerPanelId;
  renderRightActions?: Renderer;
  onFileUpload: (file: File, shouldPreventInline?: boolean) => Promise<void>;
  onAddSurveyClick: Handler;
  isAttachmentsButtonDisabled?: boolean;
  hasCsatSurveysFeature: boolean;
  shouldHideCsatSurveysButton?: boolean;
}

const StyledButton = styled(Button)`
  & + & {
    margin-left: 8px;
  }
`;
const StyledButtonContentDiv = styled.div`
  display: grid;
  grid-template-columns: auto 8px auto;
  grid-template-areas: 'icon . label';
  color: ${(p) => p.theme.greys.shade80};
`;
const StyledIconDiv = styled.div`
  grid-area: icon;
  ${center()}
`;

const StyledFileLabel = styled.label`
  display: block;

  input {
    display: none;
  }
`;

const StyledToolbarContainerDiv = styled.div`
  flex-shrink: 0;

  display: flex;
  padding: 10px 10px;

  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  background-color: ${(p) => p.theme.foreground};
  border-radius: 0 0 16px 16px;
`;

/*
 * Component.
 */

export const SettingsComposerToolbar = memo<SettingsComposerToolbarProps>((props) => {
  const {onFileUpload, onAddSurveyClick, hasCsatSurveysFeature, shouldHideCsatSurveysButton} = props;
  const [inputKey, setInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileInputChange = useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      // For each file in the input, add it.
      const {files} = event.currentTarget;
      const fileList = files && Array.from(files);
      if (fileList && fileList.length > 0) {
        fileList.forEach((f) => onFileUpload(f));
      }

      // Re-create the input to avoid existing-value issues.
      setInputKey((value) => value + 1);
    },
    [onFileUpload, setInputKey],
  );

  const maybeRenderInputFilePicker = () => {
    if (props.isAttachmentsButtonDisabled) {
      return null;
    }

    return (
      <>
        <StyledFileLabel>
          <input ref={fileInputRef} key={inputKey} type="file" onChange={onFileInputChange} multiple />
        </StyledFileLabel>
        <StyledButton variant={ButtonVariantsEnum.SECONDARY} onClick={onSimpleAttachClick}>
          <StyledButtonContentDiv>
            <StyledIconDiv>
              <Icon name="attachmentSmall" />
            </StyledIconDiv>
            <GridAreaItem area="label">
              <Trans id="composer-attachments">Attach files</Trans>
            </GridAreaItem>
          </StyledButtonContentDiv>
        </StyledButton>
      </>
    );
  };

  const maybeRenderCSATSurveyButton = () => {
    if (!hasCsatSurveysFeature || shouldHideCsatSurveysButton) {
      return null;
    }

    return (
      <StyledButton variant={ButtonVariantsEnum.SECONDARY} onClick={onAddSurveyClick}>
        <StyledButtonContentDiv>
          <StyledIconDiv>
            <Icon name="csatSmall" />
          </StyledIconDiv>
          <GridAreaItem area="label">
            <Trans id="settings-toolbar-satisfaction-button-text">Add satisfaction survey</Trans>
          </GridAreaItem>
        </StyledButtonContentDiv>
      </StyledButton>
    );
  };

  const onSimpleAttachClick = useCallback(() => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.click();
  }, []);

  function maybeRenderRightActions(renderRightActions?: Renderer): React.ReactNode {
    if (!renderRightActions) {
      return undefined;
    }
    return renderRightActions();
  }

  return (
    <StyledToolbarContainerDiv>
      {maybeRenderInputFilePicker()}
      {maybeRenderCSATSurveyButton()}
      <FlexSpacer />
      {maybeRenderRightActions(props.renderRightActions)}
    </StyledToolbarContainerDiv>
  );
});
